<template>
  <v-container fluid>
      <v-card>
          <v-toolbar dark color="secondary">
              <v-toolbar-title :style="$store.state.textFormatting.headers">
                    ASN Baseline View 
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <info>
                  <template>
                      ASN BaseLine View
                  </template>
                  <template slot="body">
                      <p> ASN baseline view gives an overview of the selected ASNs history within our system.</p>
                      <p> There are four graphs to aid understanding:</p>
                      <ul>
                          <li> ASN country breakdown (top left)</li>
                          <li> Mean Impact and Hijack score of episodes that the selected ASN has been involved in. Displayed in a histogram format. (top right)</li>
                          <li> Combined mean impact and hijack score for episodes that the selected ASN has been involved in. Displayed in a histogram format (middle right)</li>
                          <li> A count of the event types that this ASN has been involved in (bottom right)</li>
                      </ul>
                      <br>
                      <p> All bar graphs are calculated based on ASN activity within the last day</p>
                      <br />
                      <p> <b>The ASN country breakdown is calculated as follows:</b>
                        <ol>
                            <li> All prefixes from internet registries (RIPE, APNIC, AFRINIC, LACNIC, LEVEL3) that have this ASN as the owner are stored. </li>
                            <li> For each of those prefixes, we then take all prefixes with geographic (country) data associated with them, and perform the following aggregation:
                                <ol type="a">
                                    <li> If we have a direct match, take this as the (only) country information for prefix. </li>
                                    <li> Else, consider all child prefixes (more specific e.g. a /24 child of a /16 prefix), and aggregate upwards.
                                    <i> e.g. A /24 child prefix of a /23 prefix would account for half (1/2) of the IP space </i> </li>
                                    <li> Any remaining space is accounted for by either a parent prefix in registry data (e.g. a /10 parent of a /16 prefix), 
                                        or if there are no parent prefixes, the country where the ASN is registered. </li>
                                </ol>
                            </li>
                            <li> Once we have a geographical breakdown for each prefix owned by an ASN (<b>These are visible in the table above</b>), we then aggregate these again as follows:
                                <ol type="a">
                                    <li> The geographical breakdown of a given prefix is multiplied by the <b>CIDR (the number after the '/' on a prefix)</b> as an inverse power of 2. This is best explained by comparing an example (the maths is a little hard to understand). </li>
                                    <li> Let's say we have only a /32 prefix and a /31 prefix (both in IPv4 - we will come on to IPv6 later on). The /31 has twice as much IP space as the /32, so accounts for twice as much of the ASN aggregation. </li>
                                    <li> A more polarising example is if we have only a /16 prefix and a /32 prefix. Now the /16 has 2^16 = 65,536 times as much IP space as the /32, hence the /32 is almost insignificant. It is for this reason we often end up with a lot of 'Additional Countries' listed at the bottom of the geographical breakdown. </li>
                                    <li> Where we have an ASN with both IPv4 and IPv6 prefixes, we multiply the IPv4 CIDRs by 4 (since the max of IPv4 CIDRs is 32 and IPv6 is 128, 128/4 = 32). For example, a /16 IPv4 prefix is equivalent to a /48 IPv6 prefix in the calculation.</li>
                                </ol>
                            </li>
                            <li> <b>This is updated daily</b> </li>
                        </ol>
                      </p>


                  </template>
                  <template slot="action">
                      <v-btn flat color="primary" @click="routeSearchEpisodes" :style="$store.state.textFormatting.main"> View more episodes</v-btn>
                  </template>
              </info>
          </v-toolbar>
          <v-card-text>
              <v-layout wrap>
                    <v-flex xs12 md6 v-if="isCountrydata">
                        <v-flex xs12>
                            <doughnut :chart-data="doughnutData" :options="doughnutoptions"> </doughnut>
                        </v-flex>
                        <v-flex xs12 v-if="additional_countries.length > 0">
                            <v-card>
                                <v-card-text :style="$store.state.textFormatting.main">
                                    <h2 :style="$store.state.textFormatting.headers">
                                        Additional Countries (&lt;0.005%):
                                    </h2>
                                    {{ additional_countries }}
                                </v-card-text>
                            </v-card>
                        </v-flex>
                    </v-flex>
                    <v-flex xs12 md6 v-if="!isCountrydata">
                        <v-card>
                            <v-card-text>
                                <h2 :style="$store.state.textFormatting.headers">
                                    No geographical data available for this ASN
                                </h2>
                            </v-card-text>
                        </v-card>
                    </v-flex>
                    <v-flex xs12 md6>
                        <v-flex xs12>
                            <barchart :chart-data="barchartData[0]" :options="options"> </barchart>
                        </v-flex>
                        <v-flex xs12>
                            <barchart :chart-data="barchartData[1]" :options="options"> </barchart>
                        </v-flex>
                        <v-flex xs12>
                            <barchart :chart-data="barchartData[2]" :options="options"> </barchart>
                        </v-flex>
                    </v-flex>
                </v-layout>
          </v-card-text>
      </v-card>
  </v-container>
</template>

<script>

import { API, graphqlOperation } from 'aws-amplify';
import doughnut from './doughnut-graph.js'
import barchart from '../operational/BarChart.js'
import { countryNames } from '../operational/countrynames.js'
import info from '../operational/infopopout.vue'

export default {
    name: 'asn-baseline.vue',
    components: {
        doughnut,
        barchart,
        info
    },
    props: ['asn'],
    data () {
        return {
            size: "xs6",
            isCountrydata: false,
            inputasn: this.asn,
            doughnutData: null,
            selectedASN: null,
            barchartData: null,
            sampleBaselineData: [{
                event_count: 200,
                h_0_30: 60,
                h_30_60: 100,
                h_60_99: 40,
                i_0_30: 40,
                i_30_60:120,
                i_60_99: 40,
                hi_0_900: 60,
                hi_900_3600: 60,
                hi_3600_9801: 80,
                own_assert_count: 65,
                first_obs_count: 99,
                path_change_count: 166,
                restricted_dns_count: 2,
                controlled_prefix_count: 1,
                bogon_count: 3,
                route_leak_count: 8,
                country_break_down :{'US': '0.9946', 'CA': '0.0031', 'PE': '0.0013', 'TH': '0.0002', 'EC': '0.0007', 'IT': '0.0001'}
            }],
            doughnutoptions: {
                responsive: true,
                maintainAspectRatio: true,
                tooltips: {
                    titleFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10, 
                    bodyFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                },
                legend: {
                    position: 'top',
                    labels: {
                        fontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                    },
                },
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                tooltips: {
                    titleFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10, 
                    bodyFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                },
                legend: {
                    position: 'bottom',
                    labels: {
                        fontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                    },
                },
                scales: {
                    yAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Count',
                            bodyFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                        },
                        ticks: {
                            min: 0,
                            fontSize: this.$store.state.textFormatting.chart.axis_label
                        }
                    }],
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: '',
                            
                        },
                        ticks: {
                            display: false
                        }
                    }]
                }
            },
            greenscale: [
                '#A4F483',
                '#6FDC41',
                '#41C409'
            ],
            bluescale: [
                '#7093F3',
                '#3B66DC',
                '#0C3EC5'
            ],
            purplescale: [
                '#CA98F6',
                '#A452ED',
                '#7D1AD6'
            ],
            col6: [
                '#41C409',
                '#0C3EC5',
                '#7D1AD6',
                '#D68A1A',
                '#BD1AD6',
                '#D61A4B',
                '#A5B48C'
            ],
            distinct_colors: [
                '#e6194b', 
                '#3cb44b', 
                '#ffe119', 
                '#4363d8', 
                '#f58231', 
                '#911eb4', 
                '#46f0f0', 
                '#f032e6', 
                '#bcf60c', 
                '#fabebe', 
                '#008080', 
                '#e6beff', 
                '#9a6324', 
                '#fffac8', 
                '#800000', 
                '#aaffc3', 
                '#808000', 
                '#ffd8b1', 
                '#000075', 
                '#808080', 
                //'#ffffff', remove white
                '#000000'
            ],
            additional_countries: ''
        }
    },
    mounted () {
        this.getBaseLineInformation(this.inputasn)
    },
    methods: {
        routeSearchEpisodes: function () {
            this.$router.push({ name: 'Search Events, Episodes or Notifications', 
                params: {asn: this.inputasn}})
        },
        getBaseLineInformation: async function (asn) {
            var query =` query getASNBaseLine { getASNBaseline (asn: "`+asn+`", interval_length: 86400, num_of_intervals: 1, page_offset: 0 , page_rows: 1) {
                event_count
                h_0_30
                h_30_60
                h_60_99
                i_0_30
                i_30_60
                i_60_99
                hi_0_900
                hi_900_3600
                hi_3600_9801
                own_assert_count
                first_obs_count
                path_change_count
                restricted_dns_count
                bogon_count
                route_leak_count
                country_break_down
            }}`
            console.log(query)
            
            const ASNBaseLine = await API.graphql(graphqlOperation(query));
            console.log(ASNBaseLine)
            var baseLineBreakDown = ASNBaseLine.data.getASNBaseline[0]
            
            
                if (baseLineBreakDown.country_break_down) {
                var doughnutDataCountry = this.createdoughnutDataCountry(baseLineBreakDown.country_break_down)
                this.isCountrydata = true
                this.doughnutData = doughnutDataCountry
                this.size="xs12"
            } else {
                this.isCountrydata = false
            }
            
            
            
            var barChartData = this.createBarChartData(baseLineBreakDown)
            this.barchartData = barChartData
            this.size="xs12"
        },
        createdoughnutDataCountry: function (countryBD) {
            var data = { datasets: [{ data: [], backgroundColor: []}], labels: [] };

            //countryBD = JSON.parse(countryBD.replace(/'/g, '"'))
            countryBD = JSON.parse(countryBD)
            
            var countrykeys = Object.keys(countryBD);
            var country_keys_list = []
            for (var j = 0; j < countrykeys.length ; j++) {
                country_keys_list.push({
                    'code': countrykeys[j],
                    'prop': countryBD[countrykeys[j]]
                })
            }

            country_keys_list.sort(function(a, b) {
                var keyA = a.prop,
                    keyB = b.prop;
                // Compare the 2 dates
                if (keyA < keyB) return 1;
                if (keyA > keyB) return -1;
                return 0;
            });
            
            var additional_countries = []

            for (var i = 0; i < country_keys_list.length; i=i+1){
                if (country_keys_list[i].prop > 0.005) {
                    if (country_keys_list[i].code == "") {
                        data.labels.push("Unknown")
                    } else {
                        data.labels.push(countryNames[country_keys_list[i].code])
                    }
                
                    data.datasets[0].data.push((country_keys_list[i].prop*1).toFixed(2))
                    if (i < this.distinct_colors.length) {
                        var color = this.distinct_colors[i]
                    } else {
                        var color = "hsla(" + ~~(360 * Math.random()) + "," +"50%,"+"50%,1)"
                    }
                    data.datasets[0].backgroundColor.push(color)
                } else {
                    additional_countries.push(countryNames[country_keys_list[i].code])
                }
            }

            if (additional_countries.length > 0) {
                if (additional_countries.length == 1) {
                    this.additional_countries = additional_countries[0]
                } else {
                    this.additional_countries = additional_countries.join(", ")
                }
            }

            return data


        },
        createBarChartData: function (allData) {
            var hijackImpactScoreDataList = ["h_0_30", "h_30_60", "h_60_99", "i_0_30", "i_30_60", "i_60_99"]
            var hijackImpactScoreDataComboList= ["hi_0_900", "hi_900_3600", "hi_3600_9801"]
            var anomolyTypeCountsList = ["own_assert_count", "first_obs_count", "path_change_count", "restricted_dns_count",
                "controlled_prefix_count", "bogon_count", "route_leak_count"]

            var hijackImpactScoreDataListNames = ["H 0-30", "H 30-60", "H 60-99", "I 0-30", "I 30-60", "I 60-99"]
            var hijackImpactScoreDataComboListNames= ["Combined 0-900", "Combined 900-3600", "Combined 3600-9801"]
            var anomolyTypeCountsListName = ["Ownership", "First Observation", "Path Change", "Restricted DNS",
                "Controlled Prefix", "Bogon", "Route Leak"]

            

            var hijackImpactScoreData = { 
                labels: ["Hijack vs Impact"], //"h_0_30", "h_30_60", "h_60_99", "i_0_30", "i_30_60", "i_60_99" 
                datasets: [] //allData.h_0_30, allData.h_30_60, allData.h_60_99, allData.i_0_30, allData.i_30_60, allData.i_60_99 
                }

            var hijackImpactScoreDataCombo = {
                labels: ["Combined Hijack vs Impact"],
                datasets: []
            }

            var anomolyTypeCounts ={
                labels: ["Anomaly Type"],
                datasets: []
            }
            var scale;
            for (var i=0; i <hijackImpactScoreDataList.length; i = i+1) {
                if ( i < 3 ) {
                    scale = this.greenscale
                    var dataset =   {label: [hijackImpactScoreDataListNames[i]], data: [allData[hijackImpactScoreDataList[i]]], 
                    borderColor: scale[i], backgroundColor: scale[i]}
                } else {
                    scale = this.bluescale
                    var dataset =   {label: [hijackImpactScoreDataListNames[i]], data: [allData[hijackImpactScoreDataList[i]]], 
                    borderColor: scale[i - 3], backgroundColor: scale[i - 3]}
                }
                hijackImpactScoreData.datasets.push(dataset)
            }

            for (var j=0; j <hijackImpactScoreDataComboList.length; j= j+1) {

                var scale = this.purplescale
                var dataset =   {label: [hijackImpactScoreDataComboListNames[j]], data: [allData[hijackImpactScoreDataComboList[j]]], 
                borderColor: scale[j], backgroundColor: scale[j]}
                hijackImpactScoreDataCombo.datasets.push(dataset)
            }

            for (var k=0; k <anomolyTypeCountsList.length; k= k+1) {

                var scale = this.col6
                var dataset =   {label: [anomolyTypeCountsListName[k]], data: [allData[anomolyTypeCountsList[k]]], 
                borderColor: scale[k], backgroundColor: scale[k]}
                anomolyTypeCounts.datasets.push(dataset)
            }

            var BarChartData = [hijackImpactScoreData, hijackImpactScoreDataCombo, anomolyTypeCounts]



            return BarChartData
            
        },
    
    }

}
</script>

<style scoped>


    tbody tr:nth-of-type(even) {
      background-color: rgba(0, 0, 0, .05);
    }

    .v-table tr:hover:not(.v-table__expanded__content) {
        background-color: rgba(0, 0, 0, .4);
    }


</style>